import React from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { fetchWrapper, handleResponse } from 'utils/fetchWrapper';
import { useSparkRunsStatusTypes } from 'hooks/EntityTypes/useSparkRunsStatusTypes';
import { STATUS_TAGS } from 'constants/common';
import type { SingleSparkRunResponse } from 'types/SparkRun';
import { useStatusBarStore } from 'stores';

const getSparkRunsQueueJob = (sparkRunID: string | undefined | null) => {
  return fetchWrapper<SingleSparkRunResponse, true>({
    url: `SparkRuns/queue-jobs/${sparkRunID}`,
    method: 'GET',
    rawResponse: true,
  });
};

export function useSparkRunsQueueJob(
  sparkRunID: string | undefined | null,
  enabled: boolean = true
) {
  const queryClient = useQueryClient();
  const { sparkRunsStatusIdByNameMap } = useSparkRunsStatusTypes();
  const [data, setData] = React.useState<SingleSparkRunResponse | undefined>();
  const response = useQuery(
    ['SparkRunsQueueJob', sparkRunID],
    () => getSparkRunsQueueJob(sparkRunID),

    {
      onSuccess: async (response) => {
        // we only need to retry when there is not error while running spark
        if (!sparkRunsStatusIdByNameMap) {
          return;
        }
        const retryAfter = response.headers.get('Retry-After');
        const data = await handleResponse<SingleSparkRunResponse>(
          response.clone()
        );
        setData(data);
        const sparkRunAttributes = data?.data.attributes;
        const sparkRunStatusID = sparkRunAttributes.sparkRunStatusTypeID;

        const isSuccess =
          sparkRunStatusID ===
          +sparkRunsStatusIdByNameMap?.[STATUS_TAGS.SUCCESS];
        const isError =
          sparkRunStatusID === +sparkRunsStatusIdByNameMap?.[STATUS_TAGS.ERROR];
        const isComplete = percentageComplete === 100 && isSuccess;

        if (isSuccess || isError || isComplete) {
          queryClient.invalidateQueries(['HistoryChartData']);
          queryClient.invalidateQueries(['sparkRun']);
          return;
        }
        setTimeout(
          () => {
            queryClient.invalidateQueries('SparkRunsQueueJob');
          },
          retryAfter ? parseInt(retryAfter) * 1000 : 1000
        );
      },
      enabled: Boolean(sparkRunID && enabled),
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );
  const { data: sparkRunQueue } = data || {};
  const { sparkRunsStatusNameByIdMap } = useSparkRunsStatusTypes();
  const updateStatues = useStatusBarStore((state) => state.updateStatues);
  const updatePercentage = useStatusBarStore((state) => state.updatePercentage);

  const { attributes: queueJobAttributes, meta } = sparkRunQueue || {};
  const { sparkRunStatusTypeID } = queueJobAttributes || {};
  const { queueJob } = meta || {};
  const percentageComplete = queueJob?.data.attributes.percentageComplete || 0;

  React.useEffect(() => {
    if (
      !sparkRunQueue ||
      !sparkRunsStatusNameByIdMap ||
      !sparkRunStatusTypeID
    ) {
      return;
    }
    // need to update the run status when when each time queue job is fetched

    const isRunning =
      sparkRunsStatusNameByIdMap[sparkRunStatusTypeID] === STATUS_TAGS.RUNNING;
    const isPending =
      sparkRunsStatusNameByIdMap[sparkRunStatusTypeID] === STATUS_TAGS.PENDING;
    const isSuccess =
      sparkRunsStatusNameByIdMap[sparkRunStatusTypeID] === STATUS_TAGS.SUCCESS;
    const isComplete = percentageComplete === 100 && isSuccess;
    const isAttempted = isRunning || isComplete;
    const isError =
      sparkRunsStatusNameByIdMap[sparkRunStatusTypeID] === STATUS_TAGS.ERROR;
    updateStatues({
      isRunning: isRunning || isPending,
      isComplete,
      isAttempted,
      isError,
    });
    updatePercentage(percentageComplete);
    if (isComplete) {
      queryClient.invalidateQueries(['sparkRun']);
    }
  }, [
    percentageComplete,
    queryClient,
    sparkRunQueue,
    sparkRunStatusTypeID,
    sparkRunsStatusNameByIdMap,
    updatePercentage,
    updateStatues,
  ]);

  return {
    sparkRunQueue,
    percentageComplete,
    ...response,
  };
}
